import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackShareComplete } from '../ga';
import { copyShareUrl } from '../sharePayload';
import {
  facebookShareUrl,
  linkedInShareUrl,
  twitterShareUrl,
  whatsAppShareUrl,
} from '../shareUrls';
import './ShareModal.css';

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.062 2.062 0 0 1 2.063-2.063 2.063 2.063 0 0 1 2.063 2.063 2.062 2.062 0 0 1-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect
        x="9"
        y="9"
        width="13"
        height="13"
        rx="2"
        ry="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
      />
    </svg>
  );
}

/**
 * @param {{
 *   payload: { url: string, title: string, text: string };
 *   shareLocation: string;
 *   onClose: () => void;
 * }} props
 */
export function ShareModal({ payload, shareLocation, onClose }) {
  const titleId = useId();
  const urlId = useId();
  const [copyNotice, setCopyNotice] = useState(null);
  const [copyError, setCopyError] = useState(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setCopyNotice(null);
    setCopyError(null);
  }, [payload.url]);

  async function handleCopyLink() {
    setCopyError(null);
    try {
      const ok = await copyShareUrl(payload.url);
      if (ok) {
        setCopyNotice('Link copied to clipboard');
        trackShareComplete('clipboard', shareLocation);
      } else {
        setCopyError('Could not copy link. Please copy it manually.');
      }
    } catch {
      setCopyError('Could not copy link. Please copy it manually.');
    }
  }

  function handleSocialClick(method) {
    trackShareComplete(method, shareLocation);
  }

  const socialLinks = [
    {
      id: 'facebook',
      label: 'Facebook',
      href: facebookShareUrl(payload.url),
      Icon: FacebookIcon,
      className: 'share-modal__social-btn--facebook',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      href: whatsAppShareUrl(payload.url, payload.text),
      Icon: WhatsAppIcon,
      className: 'share-modal__social-btn--whatsapp',
    },
    {
      id: 'twitter',
      label: 'X',
      href: twitterShareUrl(payload.url, payload.text),
      Icon: XIcon,
      className: 'share-modal__social-btn--twitter',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: linkedInShareUrl(payload.url),
      Icon: LinkedInIcon,
      className: 'share-modal__social-btn--linkedin',
    },
  ];

  return createPortal(
    <div
      className="share-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="share-modal__header">
          <h2 id={titleId} className="share-modal__title">
            Spread the word
          </h2>
          <button
            type="button"
            className="share-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="share-modal__body">
          <p className="share-modal__lede">
            Invite friends to try Shops.mt and help us improve during beta.
          </p>

          <div className="share-modal__url-row">
            <label className="sr-only" htmlFor={urlId}>
              Link to share
            </label>
            <input
              id={urlId}
              className="share-modal__url-input"
              type="text"
              readOnly
              value={payload.url}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="share-modal__copy-btn"
              onClick={handleCopyLink}
            >
              <ClipboardIcon />
              <span>Copy link</span>
            </button>
          </div>

          {copyNotice !== null && (
            <p className="share-modal__notice" role="status">
              {copyNotice}
            </p>
          )}
          {copyError !== null && (
            <p className="share-modal__error" role="alert">
              {copyError}
            </p>
          )}

          <p className="share-modal__social-label">Share on social media</p>
          <ul className="share-modal__social-list">
            {socialLinks.map(({ id, label, href, Icon, className }) => (
              <li key={id}>
                <a
                  className={`share-modal__social-btn ${className}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick(id)}
                >
                  <Icon />
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  );
}
